import type { ReceivableRowTab } from '@klear/invoice-offer/receivables'
import type { Money } from '@klear/money'
import type { ReceivableRow } from '@klear/order-event/receivable'

export type ReceivableSearchFields = {
  texts: readonly (string | null | undefined)[]
  amounts: readonly (Money | null | undefined)[]
}

const AMOUNT_QUERY = /^[$€£]?\s*[\d,]+(\.\d*)?$/

function digitsOf(value: string): string {
  return value.replace(/[^\d.]/g, '').replace(/\.0*$/, '')
}

export function matchesReceivableSearch(fields: ReceivableSearchFields, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (needle === '') return true

  if (fields.texts.some((text) => text != null && text.toLowerCase().includes(needle))) return true

  if (!AMOUNT_QUERY.test(needle)) return false
  const wanted = digitsOf(needle)
  if (wanted === '') return false
  return fields.amounts.some((amount) => {
    if (amount == null) return false
    const formatted = amount.toString()
    return formatted.toLowerCase().includes(needle) || digitsOf(formatted).startsWith(wanted)
  })
}

function receivableIds(row: ReceivableRow): (string | null | undefined)[] {
  const purchaseOrder = row.documents.purchaseOrder.event
  return [
    row.offer?.idDisplay,
    row.invoice.orderIdDisplay,
    row.invoice.number,
    row.invoice.idDisplay,
    purchaseOrder?.number,
    purchaseOrder?.idDisplay
  ]
}

export function matchesReceivableRow(row: ReceivableRow, search: string): boolean {
  const purchaseOrder = row.documents.purchaseOrder.event
  return matchesReceivableSearch(
    {
      texts: [row.invoice.customerName, ...receivableIds(row)],
      amounts: [row.offer?.settlementCollection, row.invoice.amount, purchaseOrder?.amount]
    },
    search
  )
}

export function matchesExactly(values: readonly (string | null | undefined)[], filter: string): boolean {
  const wanted = filter.trim().toLowerCase()
  return values.some((value) => value?.trim().toLowerCase() === wanted)
}

export function matchesReceivableId(row: ReceivableRow, filter: string): boolean {
  return matchesExactly(receivableIds(row), filter)
}

export function onlyMatchingTab<Row extends { tab: ReceivableRowTab }>(
  rows: readonly Row[],
  search: string,
  matches: (row: Row, search: string) => boolean
): ReceivableRowTab | null {
  if (search.trim() === '') return null
  const found = rows.filter((row) => matches(row, search))
  return found.length === 1 ? (found[0]?.tab ?? null) : null
}
